/* GS://PROTOCOLO
 * Arquivo: src/components/forms/AdminSubscriptionForm.js
 * Descrição: Formulário para Admin alterar o plano do cliente via Cloud Function.
 * Versão: 1.1 - UI TWEAK: Exibe Plano Atual
 */

import React, { useState } from 'react';
import { getFunctions, httpsCallable } from 'firebase/functions';
import { Save, AlertCircle, Loader2, Crown, Ban } from 'lucide-react';

const PLANS = [
    { id: 'Clube Bronze', label: 'Clube Bronze (90 GP Caução)' },
    { id: 'Clube Prata', label: 'Clube Prata (225 GP Caução)' },
    { id: 'Clube Ouro', label: 'Clube Ouro (450 GP Caução)' },
    { id: 'Clube Diamante', label: 'Clube Diamante (720 GP Caução)' },
    { id: 'Aspirante', label: 'Aspirante (Sem custo)' }
];

const AdminSubscriptionForm = ({ user, onClose, onSuccess }) => {
  const [selectedPlan, setSelectedPlan] = useState('');
  const [actionType, setActionType] = useState('subscribe'); 
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState(null);
  const [successMsg, setSuccessMsg] = useState(null);

  const handleSubmit = async (e) => {
    e.preventDefault();
    setIsLoading(true);
    setError(null);
    setSuccessMsg(null);

    if (actionType === 'subscribe' && !selectedPlan) {
        setError("Selecione um plano.");
        setIsLoading(false);
        return;
    }

    try {
      const functions = getFunctions(undefined, 'southamerica-east1');
      const adminChangeUserPlan = httpsCallable(functions, 'adminChangeUserPlan');

      const response = await adminChangeUserPlan({
        targetUserId: user.uid || user.id,
        action: actionType,
        planId: selectedPlan
      });

      setSuccessMsg(response.data.message);
      
      setTimeout(() => {
        if (onSuccess) onSuccess();
        if (onClose) onClose();
      }, 2000);

    } catch (err) {
      console.error("Erro na mudança de plano:", err);
      setError(err.message || "Erro ao processar solicitação.");
    } finally {
      setIsLoading(false);
    }
  };

  return (
    <div className="p-4">
      {/* [GS-UPDATE]: Cabeçalho agora mostra o plano atual */}
      <div className="mb-6 bg-purple-50 text-purple-900 p-3 rounded text-sm flex items-center border border-purple-100">
        <Crown className="w-5 h-5 mr-2 text-purple-600" />
        <div>
            <p className="font-bold">Gestão de Assinatura</p>
            <p>Cliente: {user.displayName || user.name || user.email}</p>
            <p className="mt-1 text-xs uppercase tracking-wide text-purple-700">
                Plano Atual: <strong>{user.currentPlan || 'Não Identificado'}</strong>
            </p>
        </div>
      </div>

      {error && (
        <div className="mb-4 bg-red-50 text-red-600 p-3 rounded flex items-center text-sm">
          <AlertCircle className="w-4 h-4 mr-2 flex-shrink-0" />
          {error}
        </div>
      )}

      {successMsg && (
        <div className="mb-4 bg-green-50 text-green-700 p-3 rounded font-medium text-center border border-green-200">
          {successMsg}
        </div>
      )}

      <div className="flex gap-4 mb-6">
          <button
            type="button"
            onClick={() => { setActionType('subscribe'); setError(null); }}
            className={`flex-1 py-2 rounded-md border text-sm font-medium transition-colors ${
                actionType === 'subscribe' 
                ? 'bg-purple-600 text-white border-purple-600' 
                : 'bg-white text-gray-600 border-gray-300 hover:bg-gray-50'
            }`}
          >
            Mudar Plano
          </button>
          <button
            type="button"
            onClick={() => { setActionType('cancel'); setError(null); }}
            className={`flex-1 py-2 rounded-md border text-sm font-medium transition-colors ${
                actionType === 'cancel' 
                ? 'bg-red-600 text-white border-red-600' 
                : 'bg-white text-gray-600 border-gray-300 hover:bg-gray-50'
            }`}
          >
            Cancelar Assinatura
          </button>
      </div>

      <form onSubmit={handleSubmit} className="space-y-4">
        
        {actionType === 'subscribe' ? (
            <div className="animate-fadeIn">
                <label className="block text-sm font-medium text-gray-700 mb-2">
                    Novo Plano Desejado
                </label>
                <select
                    value={selectedPlan}
                    onChange={(e) => setSelectedPlan(e.target.value)}
                    className="w-full border border-gray-300 rounded-md px-3 py-2 focus:ring-2 focus:ring-purple-500 focus:border-transparent bg-white"
                    disabled={isLoading || successMsg}
                >
                    <option value="">Selecione...</option>
                    {PLANS.map(plan => (
                        <option key={plan.id} value={plan.id}>{plan.label}</option>
                    ))}
                </select>
                <p className="text-xs text-gray-500 mt-2">
                    Nota: O sistema irá validar o saldo do cliente e cobrar a caução automaticamente.
                </p>
            </div>
        ) : (
            <div className="p-4 bg-red-50 border border-red-100 rounded text-red-800 text-sm animate-fadeIn">
                <p className="font-bold flex items-center mb-2"><Ban className="w-4 h-4 mr-2"/> Atenção</p>
                <p>Esta ação cancelará a assinatura atual imediatamente.</p>
                <p className="mt-1">Se houver caução travada, ela será <strong>devolvida</strong> ao saldo do cliente.</p>
            </div>
        )}

        <div className="flex justify-end gap-3 pt-4 border-t mt-6">
          <button
            type="button"
            onClick={onClose}
            className="px-4 py-2 text-gray-600 hover:bg-gray-100 rounded"
            disabled={isLoading}
          >
            Fechar
          </button>
          <button
            type="submit"
            disabled={isLoading || successMsg}
            className={`px-4 py-2 text-white font-semibold rounded disabled:opacity-50 flex items-center ${
                actionType === 'subscribe' ? 'bg-purple-600 hover:bg-purple-700' : 'bg-red-600 hover:bg-red-700'
            }`}
          >
            {isLoading ? <Loader2 className="w-4 h-4 animate-spin mr-2" /> : <Save className="w-4 h-4 mr-2" />}
            {actionType === 'subscribe' ? 'Confirmar Mudança' : 'Confirmar Cancelamento'}
          </button>
        </div>
      </form>
    </div>
  );
};

export default AdminSubscriptionForm;